package com.bulut.mecitogullari.ui.screens.login

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.bulut.mecitogullari.core.i18n.APP_NAME
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.session.SessionManager
import com.bulut.mecitogullari.ui.auth.GoogleSignInContainer
import com.bulut.mecitogullari.ui.theme.AppColors
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun LoginScreen(
    onLoggedIn: () -> Unit,
    viewModel: LoginViewModel = koinViewModel(),
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val sessionUser by SessionManager.currentUser.collectAsStateWithLifecycle()
    val strings = LocalStrings.current

    // Firebase is the source of truth: navigate as soon as a session exists
    // (fresh Google sign-in or a restored session on app start).
    LaunchedEffect(sessionUser) {
        if (sessionUser != null) onLoggedIn()
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                Brush.verticalGradient(
                    listOf(Color(0xFF0B1220), AppColors.NavyDeep, Color(0xFF243B55)),
                ),
            ),
    ) {
        Skyline(modifier = Modifier.fillMaxWidth().height(220.dp).align(Alignment.BottomCenter))

        Column(
            modifier = Modifier
                .fillMaxSize()
                .safeContentPadding()
                .padding(horizontal = 28.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Spacer(modifier = Modifier.weight(1f))

            // Brand mark
            Box(
                modifier = Modifier
                    .size(76.dp)
                    .background(Color.White.copy(alpha = 0.08f), RoundedCornerShape(22.dp)),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = APP_NAME.take(1),
                    color = Color(0xFFFACC15),
                    style = MaterialTheme.typography.displaySmall,
                    fontWeight = FontWeight.Black,
                )
            }

            Text(
                text = APP_NAME,
                color = Color.White,
                style = MaterialTheme.typography.headlineMedium,
                letterSpacing = 6.sp,
                modifier = Modifier.padding(top = 20.dp),
            )
            Text(
                text = strings.loginTagline,
                color = Color.White.copy(alpha = 0.7f),
                style = MaterialTheme.typography.bodyLarge,
                modifier = Modifier.padding(top = 6.dp),
            )

            Text(
                text = strings.loginHighlights,
                color = Color(0xFFFACC15).copy(alpha = 0.9f),
                style = MaterialTheme.typography.labelLarge,
                textAlign = TextAlign.Center,
                modifier = Modifier.padding(top = 18.dp),
            )

            Spacer(modifier = Modifier.weight(1f))

            GoogleSignInContainer(
                onResult = viewModel::onSignInResult,
            ) { startSignIn ->
                GoogleButton(
                    isLoading = uiState.isLoading,
                    onClick = {
                        viewModel.onSignInStarted()
                        startSignIn()
                    },
                )
            }

            GuestButton(
                enabled = !uiState.isLoading,
                onClick = viewModel::onGuestSignIn,
            )

            uiState.error?.let { error ->
                Text(
                    text = error,
                    color = Color(0xFFFCA5A5),
                    style = MaterialTheme.typography.bodySmall,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.padding(top = 14.dp),
                )
            }

            Text(
                text = strings.loginFooter,
                color = Color.White.copy(alpha = 0.45f),
                style = MaterialTheme.typography.bodySmall,
                textAlign = TextAlign.Center,
                modifier = Modifier.padding(top = 18.dp, bottom = 40.dp),
            )
        }
    }
}

@Composable
private fun GoogleButton(
    isLoading: Boolean,
    onClick: () -> Unit,
) {
    Surface(
        onClick = onClick,
        enabled = !isLoading,
        shape = RoundedCornerShape(14.dp),
        color = Color.White,
        modifier = Modifier.fillMaxWidth().height(54.dp),
    ) {
        Row(
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxSize(),
        ) {
            if (isLoading) {
                CircularProgressIndicator(
                    color = AppColors.Navy,
                    modifier = Modifier.size(22.dp),
                )
            } else {
                Text(
                    text = "G",
                    color = Color(0xFF4285F4),
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Black,
                )
                Text(
                    text = LocalStrings.current.continueWithGoogle,
                    color = AppColors.NavyDeep,
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.padding(start = 10.dp),
                )
            }
        }
    }
}

@Composable
private fun GuestButton(
    enabled: Boolean,
    onClick: () -> Unit,
) {
    Surface(
        onClick = onClick,
        enabled = enabled,
        shape = RoundedCornerShape(14.dp),
        color = Color.White.copy(alpha = 0.08f),
        modifier = Modifier.fillMaxWidth().padding(top = 12.dp).height(54.dp),
    ) {
        Row(
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxSize(),
        ) {
            Text(
                text = LocalStrings.current.continueAsGuest,
                color = Color.White.copy(alpha = 0.9f),
                style = MaterialTheme.typography.titleMedium,
            )
        }
    }
}

/** Subtle construction skyline silhouette with a crane. */
@Composable
private fun Skyline(modifier: Modifier = Modifier) {
    Canvas(modifier = modifier) {
        val w = size.width
        val h = size.height
        val silhouette = Color.White.copy(alpha = 0.05f)
        val silhouetteLight = Color.White.copy(alpha = 0.08f)

        fun building(xFraction: Float, widthFraction: Float, heightFraction: Float, color: Color) {
            val bw = w * widthFraction
            val bh = h * heightFraction
            drawRect(
                color = color,
                topLeft = Offset(w * xFraction, h - bh),
                size = Size(bw, bh),
            )
        }

        building(0.00f, 0.14f, 0.55f, silhouette)
        building(0.13f, 0.10f, 0.80f, silhouetteLight)
        building(0.26f, 0.16f, 0.45f, silhouette)
        building(0.44f, 0.12f, 0.95f, silhouette)
        building(0.58f, 0.14f, 0.60f, silhouetteLight)
        building(0.74f, 0.11f, 0.75f, silhouette)
        building(0.87f, 0.13f, 0.50f, silhouetteLight)

        // Tower crane above the tallest block
        val craneColor = Color(0xFFFACC15).copy(alpha = 0.35f)
        val mastX = w * 0.50f
        val mastTop = h * 0.02f
        drawLine(craneColor, Offset(mastX, h - h * 0.95f), Offset(mastX, mastTop), strokeWidth = 3f)
        drawLine(craneColor, Offset(mastX - w * 0.16f, mastTop), Offset(mastX + w * 0.24f, mastTop), strokeWidth = 3f)
        drawLine(craneColor, Offset(mastX + w * 0.20f, mastTop), Offset(mastX + w * 0.20f, h * 0.22f), strokeWidth = 2f)
    }
}
